(function () {
  'use strict';

  /** @type {Window & Record<string, any>} */
  const runtimeWindow = window;
  const INIT_FLAG = '__aoneGoldTickerScriptInitialized';
  const CONTROLLER_KEY = '__aoneGoldTickerController';
  const CONTAINER_SELECTOR = '[data-gold-ticker-container]';
  const TRACK_SELECTOR = '[data-gold-ticker-track]';
  const GROUP_SELECTOR = '[data-gold-ticker-group]';

  const PURITY_MULTIPLIERS = Object.freeze({
    '24k': 1,
    '22k': 0.916,
    '18k': 0.75,
    '14k': 0.585,
    '10k': 0.417,
  });

  const ORDERED_KEYS = Object.freeze(['spot', '10k', '14k', '18k', '22k', '24k']);

  if (runtimeWindow[INIT_FLAG]) return;
  runtimeWindow[INIT_FLAG] = true;

  class GoldTickerController {
    constructor() {
      /** @type {number | null} */
      this.intervalId = null;
      /** @type {HTMLElement | null} */
      this.container = null;
      /** @type {HTMLElement | null} */
      this.track = null;
      /** @type {HTMLElement | null} */
      this.group = null;
      /** @type {AbortController | null} */
      this.abortController = null;
      /** @type {boolean} */
      this.fetchInFlight = false;
      /** @type {{ enabled: boolean; livePriceEnabled: boolean; baseUrl: string; autoRefresh: boolean; intervalMs: number; animationEnabled: boolean; speed: string; currencySymbol: string; spotLabel: string; visibleKeys: string[] } | null} */
      this.config = null;
      /** @type {string} */
      this.configSignature = '';
      /** @type {number | null} */
      this.lastSpotPrice = null;
      /** @type {string} */
      this.lastRenderedSignature = '';
    }

    /** @param {HTMLElement} container */
    mount(container) {
      if (!(container instanceof HTMLElement)) return;

      const track = container.querySelector(TRACK_SELECTOR);
      const group = container.querySelector(GROUP_SELECTOR);
      if (!(track instanceof HTMLElement) || !(group instanceof HTMLElement)) return;

      const config = this.readConfig(container);
      const configSignature = this.createConfigSignature(config);

      const sameTarget = this.container === container && this.configSignature === configSignature;
      if (sameTarget) return;

      this.unmount(false);

      this.container = container;
      this.track = track;
      this.group = group;
      this.config = config;
      this.configSignature = configSignature;

      this.applySpeedClass(config.speed);
      this.applyAnimationMode(config.animationEnabled);

      if (!config.enabled) {
        return;
      }

      if (this.lastSpotPrice !== null) {
        this.renderFromSpot(this.lastSpotPrice);
      } else {
        this.renderUnavailable();
      }

      if (!config.livePriceEnabled) return;

      this.refresh();
      if (config.autoRefresh) {
        this.intervalId = window.setInterval(() => {
          this.refresh();
        }, config.intervalMs);
      }
    }

    /**
     * @param {boolean} clearRenderState
     */
    unmount(clearRenderState = true) {
      this.clearInterval();
      this.abortController?.abort();
      this.abortController = null;
      this.fetchInFlight = false;

      if (clearRenderState) {
        this.lastRenderedSignature = '';
      }

      this.container = null;
      this.track = null;
      this.group = null;
      this.config = null;
      this.configSignature = '';
    }

    clearInterval() {
      if (this.intervalId === null) return;
      window.clearInterval(this.intervalId);
      this.intervalId = null;
    }

    /** @param {HTMLElement} container */
    readConfig(container) {
      /** @param {string | undefined} value */
      const parseBoolean = (value) => value === 'true';
      const rawInterval = Number.parseInt(container.dataset.goldPriceRefreshInterval || '', 10);
      const intervalSeconds = Number.isFinite(rawInterval) && rawInterval > 0 ? rawInterval : 60;
      const speed = (container.dataset.goldTickerSpeed || 'slow').toLowerCase();
      const spotLabel = (container.dataset.goldTickerLabelSpot || 'Gold Spot').trim() || 'Gold Spot';
      const currencySymbol = (container.dataset.goldTickerCurrencySymbol || '$').trim() || '$';

      /** @type {Record<string, boolean>} */
      const visibilityMap = {
        spot: parseBoolean(container.dataset.goldTickerShowSpot),
        '10k': parseBoolean(container.dataset.goldTickerShowTenK),
        '14k': parseBoolean(container.dataset.goldTickerShowFourteenK),
        '18k': parseBoolean(container.dataset.goldTickerShowEighteenK),
        '22k': parseBoolean(container.dataset.goldTickerShowTwentyTwoK),
        '24k': parseBoolean(container.dataset.goldTickerShowTwentyFourK),
      };

      const visibleKeys = ORDERED_KEYS.filter((key) => visibilityMap[key]);

      return {
        enabled: parseBoolean(container.dataset.goldTickerEnabled),
        livePriceEnabled: parseBoolean(container.dataset.goldPriceEnabled),
        baseUrl: (container.dataset.goldPriceApiBaseUrl || '').trim(),
        autoRefresh: parseBoolean(container.dataset.goldPriceAutoRefresh),
        intervalMs: intervalSeconds * 1000,
        animationEnabled: parseBoolean(container.dataset.goldTickerAnimation),
        speed: ['slow', 'medium', 'fast'].includes(speed) ? speed : 'slow',
        currencySymbol,
        spotLabel,
        visibleKeys: visibleKeys.length > 0 ? visibleKeys : ['spot', '24k'],
      };
    }

    /**
     * @param {{ enabled: boolean; livePriceEnabled: boolean; baseUrl: string; autoRefresh: boolean; intervalMs: number; animationEnabled: boolean; speed: string; currencySymbol: string; spotLabel: string; visibleKeys: string[] }} config
     */
    createConfigSignature(config) {
      return [
        config.enabled,
        config.livePriceEnabled,
        config.baseUrl,
        config.autoRefresh,
        config.intervalMs,
        config.animationEnabled,
        config.speed,
        config.currencySymbol,
        config.spotLabel,
        config.visibleKeys.join(','),
      ].join('|');
    }

    /** @param {string} speed */
    applySpeedClass(speed) {
      if (!this.container) return;

      this.container.classList.remove(
        'aone-gold-ticker--speed-slow',
        'aone-gold-ticker--speed-medium',
        'aone-gold-ticker--speed-fast'
      );
      this.container.classList.add(`aone-gold-ticker--speed-${speed}`);
    }

    /** @param {boolean} animationEnabled */
    applyAnimationMode(animationEnabled) {
      if (!this.container || !this.track || !this.group) return;

      this.container.classList.toggle('aone-gold-ticker--animated', animationEnabled);
    }

    /** @param {string} baseUrl */
    buildEndpoint(baseUrl) {
      return `${baseUrl.replace(/\/+$/, '')}/api/gold-price/latest`;
    }

    async refresh() {
      if (!this.config || !this.container || this.fetchInFlight) return;
      if (!this.config.livePriceEnabled || !this.config.baseUrl) {
        this.renderUnavailable();
        return;
      }

      this.fetchInFlight = true;
      this.abortController?.abort();
      this.abortController = new AbortController();

      try {
        const response = await fetch(this.buildEndpoint(this.config.baseUrl), {
          method: 'GET',
          headers: { accept: 'application/json' },
          signal: this.abortController.signal,
        });

        if (!response.ok) {
          throw new Error(`Gold ticker request failed with status ${response.status}`);
        }

        const payload = await response.json();
        const spotPrice = this.extractSpotPrice(payload);
        if (spotPrice === null) {
          throw new Error('Gold ticker response missing a numeric spot price');
        }

        if (this.lastSpotPrice !== spotPrice) {
          this.lastSpotPrice = spotPrice;
          this.renderFromSpot(spotPrice);
        }
      } catch (error) {
        if (error?.name !== 'AbortError') {
          if (this.lastSpotPrice === null) {
            this.renderUnavailable();
          }
          console.error('[gold-ticker] Failed to refresh ticker', error);
        }
      } finally {
        this.fetchInFlight = false;
      }
    }

    /** @param {any} payload */
    extractSpotPrice(payload) {
      const candidates = [
        payload?.data?.latest?.liveGoldPrice,
        payload?.data?.latest?.goldPM,
        payload?.data?.latest?.goldAM,
        payload?.data?.last20SuccessfulUpdates?.[0]?.liveGoldPrice,
        payload?.data?.last20SuccessfulUpdates?.[0]?.goldPM,
        payload?.data?.last20SuccessfulUpdates?.[0]?.goldAM,
        payload?.goldPrice,
        payload?.gold_price,
        payload?.price,
        payload?.latestPrice,
        payload?.latest_price,
        payload?.data?.price,
        payload?.data?.goldPrice,
        payload?.data?.gold_price,
      ];

      for (const candidate of candidates) {
        const numberValue = this.toNumber(candidate);
        if (numberValue !== null) return numberValue;
      }

      return null;
    }

    /** @param {unknown} value */
    toNumber(value) {
      if (typeof value === 'number' && Number.isFinite(value)) return value;
      if (typeof value === 'string') {
        const normalized = value.replace(/,/g, '').replace(/[^0-9.-]/g, '');
        if (!normalized) return null;
        const parsed = Number.parseFloat(normalized);
        return Number.isFinite(parsed) ? parsed : null;
      }
      return null;
    }

    /** @param {number} spotPrice */
    calculatePrices(spotPrice) {
      return {
        spot: spotPrice,
        '10k': spotPrice * PURITY_MULTIPLIERS['10k'],
        '14k': spotPrice * PURITY_MULTIPLIERS['14k'],
        '18k': spotPrice * PURITY_MULTIPLIERS['18k'],
        '22k': spotPrice * PURITY_MULTIPLIERS['22k'],
        '24k': spotPrice * PURITY_MULTIPLIERS['24k'],
      };
    }

    /**
     * @param {number | null} value
     * @param {string} currencySymbol
     */
    formatValue(value, currencySymbol) {
      if (value === null || !Number.isFinite(value)) return `${currencySymbol}--`;
      return `${currencySymbol}${new Intl.NumberFormat(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value)}`;
    }

    renderUnavailable() {
      if (!this.config) return;
      const values = {
        spot: null,
        '10k': null,
        '14k': null,
        '18k': null,
        '22k': null,
        '24k': null,
      };
      this.render(values);
    }

    /** @param {number} spotPrice */
    renderFromSpot(spotPrice) {
      this.render(this.calculatePrices(spotPrice));
    }

    /** @param {Record<string, number | null>} values */
    render(values) {
      if (!this.group || !this.config) return;

      const html = this.config.visibleKeys
        .map((key) => this.renderItemHtml(key, values[key] ?? null))
        .join('');
      const nextSignature = `${this.config.visibleKeys.join(',')}|${Object.values(values)
        .map((value) => (value === null ? '--' : value.toFixed(4)))
        .join('|')}|${this.config.currencySymbol}|${this.config.spotLabel}`;

      if (nextSignature === this.lastRenderedSignature) return;

      this.group.innerHTML = html;
      this.lastRenderedSignature = nextSignature;
    }

    /**
     * @param {string} key
     * @param {number | null} value
     */
    renderItemHtml(key, value) {
      if (!this.config) return '';

      const label = key === 'spot' ? `${this.config.spotLabel}:` : `${key.toUpperCase()}:`;
      const isSpot = key === 'spot';
      const valueClass = isSpot ? 'aone-gold-ticker__value aone-gold-ticker__value--spot' : 'aone-gold-ticker__value';
      const textValue = this.formatValue(value, this.config.currencySymbol);

      return `<span class="aone-gold-ticker__item" data-gold-key="${key}"><span class="aone-gold-ticker__label">${label}</span><span class="${valueClass}">${textValue}</span></span>`;
    }
  }

  /** @type {GoldTickerController} */
  const controller = runtimeWindow[CONTROLLER_KEY] || new GoldTickerController();
  runtimeWindow[CONTROLLER_KEY] = controller;

  /** @param {Document | HTMLElement | unknown} scope */
  function init(scope) {
    const root = scope instanceof Document || scope instanceof HTMLElement ? scope : document;
    const container = root.querySelector(CONTAINER_SELECTOR);
    if (!(container instanceof HTMLElement)) return;
    controller.mount(container);
  }

  /** @param {Event} event */
  function handleSectionUnload(event) {
    const target = /** @type {EventTarget | null} */ (event.target);
    if (!(target instanceof HTMLElement)) return;
    if (!target.querySelector(CONTAINER_SELECTOR)) return;
    controller.unmount();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', handleSectionUnload);
})();
